import proj4 from 'proj4';

import { getProj4Definition } from '../../../utils/proj4/projections';
import { CogSource } from './cog-source';

export type Point2 = [number, number];

/** proj4 flags that don't change the projection, so they shouldn't affect the comparison. */
const IGNORED_PROJ4_TOKENS = new Set(['+no_defs', '+type=crs', '+wktext']);

/**
 * Normalizes a proj4 definition for comparison: sorted tokens, numbers in canonical form
 * (so +k=1 equals +k=1.0) and no-op flags dropped.
 */
export function normalizeProj4(definition: string): string {
  return definition
    .trim()
    .split(/\s+/)
    .filter((token) => !IGNORED_PROJ4_TOKENS.has(token))
    .map((token) => {
      const [key, value] = token.split('=');
      return value !== undefined && value !== '' && !isNaN(Number(value))
        ? `${key}=${Number(value)}`
        : token;
    })
    .sort()
    .join(' ');
}

export type CogProjection = {
  /** proj4 definition of the COG's CRS. */
  definition: string;
  /** Where the definition came from. */
  origin: 'embedded' | 'project';
  /** True when the COG is already in the project CRS, so tiles need no reprojection. */
  sameAsProject: boolean;
  toProject: (point: Point2) => Point2;
  toWgs84: (point: Point2) => Point2;
};

/**
 * Picks the COG's CRS and builds the transforms to the project CRS and WGS84.
 *
 * The SKYMAP_PROJ4 item our pipeline embeds in the file wins. Without it the COG is assumed to
 * be in the project CRS, which is what the backend converts uploads to. When the COG and project
 * CRS are the same, toProject is the identity and proj4 is skipped.
 */
export function resolveCogProjection(params: {
  embeddedProj4: string | undefined;
  projectProjection: string;
  /** Only used in the warning. */
  epsg?: number;
}): CogProjection {
  const { embeddedProj4, projectProjection, epsg } = params;

  if (!embeddedProj4) {
    console.warn(
      `COG has no embedded SKYMAP_PROJ4 (EPSG:${epsg ?? 'unknown'}); assuming the project CRS`,
    );
  }
  const definition = embeddedProj4 ?? getProj4Definition(projectProjection);
  const sameAsProject =
    normalizeProj4(definition) === normalizeProj4(getProj4Definition(projectProjection));
  const converter = proj4(definition, projectProjection);
  const wgs84Converter = proj4(definition, 'wgs84');

  return {
    definition,
    origin: embeddedProj4 ? 'embedded' : 'project',
    sameAsProject,
    toProject: sameAsProject ? (point) => point : (point) => converter.forward(point),
    toWgs84: (point) => wgs84Converter.forward(point),
  };
}

/** Bounds, origo and the asset all need the same transforms; resolve them once per source. */
const projectionsBySource = new WeakMap<CogSource, Map<string, CogProjection>>();

export function getCogProjection(source: CogSource, projectProjection: string): CogProjection {
  let byProjection = projectionsBySource.get(source);
  if (!byProjection) {
    byProjection = new Map();
    projectionsBySource.set(source, byProjection);
  }
  let projection = byProjection.get(projectProjection);
  if (!projection) {
    projection = resolveCogProjection({
      embeddedProj4: source.proj4,
      projectProjection,
      epsg: source.epsg,
    });
    byProjection.set(projectProjection, projection);
  }
  return projection;
}

/** The COG's corners (SW, SE, NE, NW) in project coordinates and in WGS84 lon/lat. */
export function getCogCorners(
  source: CogSource,
  projectProjection: string,
): { project: Point2[]; wgs84: Point2[] } {
  const projection = getCogProjection(source, projectProjection);
  const b = source.getBounds();
  const corners: Point2[] = [
    [b.minX, b.minY],
    [b.maxX, b.minY],
    [b.maxX, b.maxY],
    [b.minX, b.maxY],
  ];
  return {
    project: corners.map(projection.toProject),
    wgs84: corners.map(projection.toWgs84),
  };
}
